import { create } from 'zustand';
import {
  registerVisit,
  fetchVisits,
  fetchDailyVisits,
  updateVisit,
  skipVisit,
  requeueVisit,
  fetchDoctors,
  searchPatientByMobile,
  fetchConsultationStats,
} from '../services/api/visitApi';
import { realtimeMiddleware, createOptimisticUpdate, statusToEventType } from './realtimeMiddleware';
import eventBus from '../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';
import { updateDoctorAvailability as apiUpdateDoctorAvailability } from '../services/api/staffApi';
import { coalesce } from '../utils/coalesce';
import { DATA_KEYS } from './dataKeys';

// ── Request coalescing ───────────────────────────────────────────────
// fetchDailyVisits / fetchDoctors / loadConsultationStats are wrapped in
// per-slice in-flight coalescing (utils/coalesce.js): when ~12 dashboard
// widgets mount at once and each calls these, the calls collapse to one
// request, and on the dashboard they piggyback on the single
// dashboard-summary request (services/dashboardHydrator.js). Pass
// { force: true } to bypass coalescing (the hydrator's fallback path).

// ── Optimistic registration protection ───────────────────────────────
// After registerVisit, we keep recently-added visit IDs so that
// fetchDailyVisits (which queries an eventually-consistent GSI) won't
// overwrite them before DynamoDB propagates the write.
let _recentRegistrations = [];   // [{ id, addedAt }]
const REGISTRATION_GUARD_MS = 10_000; // Protect for 10 seconds

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Visit Store — Zustand state management for visits
 *
 * Now with REALTIME SYNC:
 *   • Optimistic UI updates — instant feedback, rollback on error
 *   • Event emission — local mutations broadcast to other tabs/users
 *   • Event handlers — remote/cross-tab events patch state surgically
 *   • Reconciliation — re-fetches server truth on reconnect
 *
 * Backend endpoints used:
 *   POST   /api/organisation/visits         → registerVisit
 *   GET    /api/organisation/visits          → fetchVisits (filters)
 *   GET    /api/organisation/visits/daily    → fetchDailyVisits
 *   PUT    /api/organisation/visits/:id      → updateVisit (status change)
 *   GET    /api/organisation/mem?role=DOCTOR → fetchDoctors
 *   GET    /api/organisation/patients?mobile → searchPatientByMobile
 * ═══════════════════════════════════════════════════════════════════════
 */

// ── Realtime Event Handlers ──────────────────────────────────────────
// These pure functions receive (currentState, event) → partialState.
// They run when a remote or cross-tab event arrives.
// They NEVER run for 'local' source events (those were already applied
// optimistically by the action that caused them).
const visitEventHandlers = {
  [REALTIME_EVENTS.VISIT_CREATED]: (state, event) => {
    const newVisit = event.payload?.visit;
    if (!newVisit) {
      console.warn('[VisitStore] VISIT_CREATED: no visit in payload', event);
      return {};
    }

    const visitId = newVisit.id || newVisit.visit_id;
    const visitDoctorId = newVisit.doctor_member_id || newVisit.visit_doctor_id;

    // Scope-aware: if viewing a specific doctor's queue, only add matching visits
    const scopedDoctorId = state.scope?.doctorId;
    if (scopedDoctorId && visitDoctorId !== scopedDoctorId) {
      console.log(`[VisitStore] VISIT_CREATED: skipped — scope=${scopedDoctorId}, visit_doctor=${visitDoctorId}`);
      return {}; // Visit belongs to a different doctor — skip
    }

    // ── Protect remote-delivered visits from GSI eventual consistency ──
    // Same guard used by registerVisit() — without this, a fetchDailyVisits
    // that fires before the GSI propagates will silently drop the new visit.
    if (visitId && !_recentRegistrations.some((r) => r.id === visitId)) {
      _recentRegistrations.push({ id: visitId, addedAt: Date.now() });
    }

    // Avoid duplicate: check if visit already exists
    const existsInDaily = state.dailyVisits.some(
      (v) => (v.id || v.visit_id) === visitId
    );
    const existsInVisits = state.visits.some(
      (v) => (v.id || v.visit_id) === visitId
    );

    const patch = {};
    if (!existsInDaily) {
      patch.dailyVisits = [...state.dailyVisits, newVisit];
    }
    if (!existsInVisits) {
      patch.visits = [...state.visits, newVisit];
    }

    console.log(`[VisitStore] VISIT_CREATED: id=${visitId}, source=${event.source}, added=${!existsInDaily}, total=${(patch.dailyVisits || state.dailyVisits).length}`);
    return patch;
  },

  [REALTIME_EVENTS.VISIT_STATUS_CHANGED]: (state, event) => {
    const { visitId, newStatus, visit: updatedVisit } = event.payload || {};
    if (!visitId) return {};

    const patchVisit = (list) =>
      list.map((v) => {
        if ((v.id || v.visit_id) === visitId) {
          return updatedVisit ? { ...v, ...updatedVisit } : { ...v, status: newStatus };
        }
        return v;
      });

    return {
      dailyVisits: patchVisit(state.dailyVisits),
      visits: patchVisit(state.visits),
    };
  },

  [REALTIME_EVENTS.VISIT_UPDATED]: (state, event) => {
    const { visitId, visit: updatedVisit } = event.payload || {};
    if (!visitId || !updatedVisit) return {};

    const scopedDoctorId = state.scope?.doctorId;
    const updatedDoctorId = updatedVisit.doctor_member_id || updatedVisit.visit_doctor_id;

    // Scope-aware: handle doctor reassignment
    if (scopedDoctorId && updatedDoctorId) {
      if (updatedDoctorId !== scopedDoctorId) {
        // Doctor reassigned AWAY from this scope → remove from lists
        return {
          dailyVisits: state.dailyVisits.filter(
            (v) => (v.id || v.visit_id) !== visitId
          ),
          visits: state.visits.filter(
            (v) => (v.id || v.visit_id) !== visitId
          ),
        };
      }

      // Doctor reassigned TO this scope → add if new, patch if exists
      const addOrPatch = (list) => {
        const exists = list.some((v) => (v.id || v.visit_id) === visitId);
        if (exists) {
          return list.map((v) =>
            (v.id || v.visit_id) === visitId ? { ...v, ...updatedVisit } : v
          );
        }
        return [...list, updatedVisit];
      };

      return {
        dailyVisits: addOrPatch(state.dailyVisits),
        visits: addOrPatch(state.visits),
      };
    }

    // No scope or no doctor change — patch in place
    const patchVisit = (list) =>
      list.map((v) => {
        if ((v.id || v.visit_id) === visitId) {
          return { ...v, ...updatedVisit };
        }
        return v;
      });

    return {
      dailyVisits: patchVisit(state.dailyVisits),
      visits: patchVisit(state.visits),
    };
  },

  [REALTIME_EVENTS.VISIT_HANDLER_CHANGED]: (state, event) => {
    // Phase 1b — patch the active_handler_* fields on matching visits so
    // queue rows flip their "Handed off to X" badge live without a refetch.
    const {
      visit_id: visitId,
      active_handler_member_id,
      active_handler_role,
      sitting_id,
    } = event.payload || {};
    if (!visitId) return {};

    const patch = {
      active_handler_member_id: active_handler_member_id ?? null,
      active_handler_role: active_handler_role ?? null,
      active_handler_sitting_id: active_handler_member_id ? (sitting_id ?? null) : null,
    };

    const patchVisit = (list) =>
      list.map((v) =>
        (v.id || v.visit_id) === visitId ? { ...v, ...patch } : v
      );

    return {
      dailyVisits: patchVisit(state.dailyVisits),
      visits: patchVisit(state.visits),
    };
  },

  [REALTIME_EVENTS.VISIT_DELETED]: (state, event) => {
    const { visitId } = event.payload || {};
    if (!visitId) return {};

    return {
      dailyVisits: state.dailyVisits.filter(
        (v) => (v.id || v.visit_id) !== visitId
      ),
      visits: state.visits.filter(
        (v) => (v.id || v.visit_id) !== visitId
      ),
    };
  },

  [REALTIME_EVENTS.QUEUE_STARTED]: (state, event) => {
    // Reuse VISIT_STATUS_CHANGED handler
    return visitEventHandlers[REALTIME_EVENTS.VISIT_STATUS_CHANGED](state, {
      ...event,
      payload: { ...event.payload, newStatus: 'IN_PROGRESS' },
    });
  },

  [REALTIME_EVENTS.QUEUE_SKIPPED]: (state, event) => {
    return visitEventHandlers[REALTIME_EVENTS.VISIT_STATUS_CHANGED](state, {
      ...event,
      payload: { ...event.payload, newStatus: 'CANCELLED' },
    });
  },

  [REALTIME_EVENTS.VISIT_SKIPPED]: (state, event) => {
    // Handle skip: patch status to SKIPPED + set was_skipped flag
    return visitEventHandlers[REALTIME_EVENTS.VISIT_STATUS_CHANGED](state, {
      ...event,
      payload: { ...event.payload, newStatus: 'SKIPPED' },
    });
  },

  [REALTIME_EVENTS.VISIT_REQUEUED]: (state, event) => {
    // Handle requeue: patch status back to QUEUE with new queue_number
    const { visitId, visit: updatedVisit } = event.payload || {};
    if (!visitId) return {};

    const patchVisit = (list) =>
      list.map((v) => {
        if ((v.id || v.visit_id) === visitId) {
          return updatedVisit ? { ...v, ...updatedVisit, status: 'QUEUE' } : { ...v, status: 'QUEUE' };
        }
        return v;
      });

    return {
      dailyVisits: patchVisit(state.dailyVisits),
      visits: patchVisit(state.visits),
    };
  },

  [REALTIME_EVENTS.BULK_REFRESH]: (state, event) => {
    const { dailyVisits, visits } = event.payload || {};
    const patch = {};
    if (dailyVisits) patch.dailyVisits = dailyVisits;
    if (visits) patch.visits = visits;
    return patch;
  },

  // ── Doctor status (availability) ─────────────────────────
  [REALTIME_EVENTS.DOCTOR_STATUS_CHANGED]: (state, event) => {
    const { memberId, availability } = event.payload || {};
    if (!memberId) return {};

    return {
      doctors: (state.doctors || []).map((d) =>
        (d.member_id || d.doctor_id) === memberId
          ? { ...d, availability }
          : d
      ),
    };
  },

  // Alias — backend may send either event type
  [REALTIME_EVENTS.DOCTOR_AVAILABILITY_CHANGED]: (state, event) => {
    const { memberId, availability } = event.payload || {};
    if (!memberId) return {};

    return {
      doctors: (state.doctors || []).map((d) =>
        (d.member_id || d.doctor_id) === memberId
          ? { ...d, availability }
          : d
      ),
    };
  },

  // ── Staff profile updates ────────────────────────────────
  [REALTIME_EVENTS.STAFF_UPDATED]: (state, event) => {
    const { memberId, updates } = event.payload || {};
    if (!memberId || !updates) return {};

    return {
      doctors: (state.doctors || []).map((d) =>
        (d.member_id || d.doctor_id) === memberId
          ? { ...d, ...updates }
          : d
      ),
    };
  },

  // ── Phase 6.0 / 10 — Co-worker visit sharing ────────────
  // When a co-worker is added to a visit, the co-worker's per-doctor
  // GSI2 lane gains a projection row. The shared visit shows up in
  // their daily list on the next fetch. We bust the cooldown so the
  // co-worker sees the visit immediately, instead of waiting for the
  // 60s reconciliation tick.
  [REALTIME_EVENTS.VISIT_COWORKER_ADDED]: (state, event) => {
    const scopedDoctorId = state.scope?.doctorId;
    const targetMemberId = event.payload?.memberId;
    if (scopedDoctorId && targetMemberId === scopedDoctorId) {
      // force: realtime echo needs post-change truth, not a stale in-flight.
      queueMicrotask(() => useVisitStore.getState().fetchDailyVisits(undefined, { force: true }));
    }
    return {};
  },

  [REALTIME_EVENTS.VISIT_COWORKER_REMOVED]: (state, event) => {
    const scopedDoctorId = state.scope?.doctorId;
    const targetMemberId = event.payload?.memberId;
    const visitId = event.payload?.visitId;
    // Removed co-worker drops the shared visit from their list locally.
    if (scopedDoctorId && targetMemberId === scopedDoctorId && visitId) {
      return {
        dailyVisits: state.dailyVisits.filter(
          (v) => (v.id || v.visit_id) !== visitId,
        ),
        visits: state.visits.filter(
          (v) => (v.id || v.visit_id) !== visitId,
        ),
      };
    }
    return {};
  },

  // Phase 6.0 — Receptionist→Doctor RESOLVED that swaps the primary
  // doctor. Prior primary becomes a co-worker (server-side). Both
  // doctors' lanes refresh.
  [REALTIME_EVENTS.VISIT_DOCTOR_CHANGED]: (state, event) => {
    const { visitId, fromDoctorId, toDoctorId, visit: updatedVisit } =
      event.payload || {};
    if (!visitId) return {};
    const scopedDoctorId = state.scope?.doctorId;
    if (
      scopedDoctorId
      && (scopedDoctorId === fromDoctorId || scopedDoctorId === toDoctorId)
    ) {
      // force: realtime echo needs post-change truth, not a stale in-flight.
      queueMicrotask(() => useVisitStore.getState().fetchDailyVisits(undefined, { force: true }));
    }
    // Even without scope, patch existing rows in place if present.
    if (!updatedVisit) return {};
    const patchVisit = (list) =>
      list.map((v) =>
        (v.id || v.visit_id) === visitId ? { ...v, ...updatedVisit } : v,
      );
    return {
      dailyVisits: patchVisit(state.dailyVisits),
      visits: patchVisit(state.visits),
    };
  },
};

// ── Store Definition ─────────────────────────────────────────────────
const useVisitStore = create(
  realtimeMiddleware(
    (set, get) => ({
      // ── State ────────────────────────────────────────────
      visits: [],
      doctors: [],
      dailyVisits: [],
      consultationStats: null,
      patientLookup: null,
      createdVisit: null,
      loading: {
        visits: false,
        doctors: false,
        patient: false,
        register: false,
        update: false,
        daily: false,
        stats: false,
      },
      error: null,
      successMessage: null,

      // ── Scope ────────────────────────────────────────────
      scope: { doctorId: null },
      setScope: (doctorId) => {
        const current = get().scope;
        const next = doctorId || null;
        // Only update if value actually changed — prevents object reference churn
        if (current.doctorId !== next) {
          set({ scope: { doctorId: next } });
          // Scope changed — force a re-fetch (bypass coalescing) so all
          // components see correctly-scoped data instead of piggybacking a
          // stale/unscoped in-flight request.
          // Async to avoid calling fetchDailyVisits inside a render cycle
          queueMicrotask(() => get().fetchDailyVisits(undefined, { force: true }));
        }
      },

      // ── Helpers ──────────────────────────────────────────
      setLoading: (key, value) =>
        set((s) => ({ loading: { ...s.loading, [key]: value } })),

      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      clearSuccess: () => set({ successMessage: null }),
      clearCreatedVisit: () => set({ createdVisit: null }),

      /**
       * Patch arbitrary fields on a visit in both `dailyVisits` and `visits` arrays.
       * Used after inline edits (e.g. patient demographics) to reflect changes
       * immediately without a full refetch.
       *
       * @param {string} visitId
       * @param {object} fields  — e.g. { patient_name, patient_age, patient_gender }
       */
      patchVisitFields: (visitId, fields) => {
        if (!visitId || !fields) return;
        const patcher = (list) =>
          list.map((v) =>
            (v.id || v.visit_id) === visitId ? { ...v, ...fields } : v
          );
        set((s) => ({
          dailyVisits: patcher(s.dailyVisits),
          visits: patcher(s.visits),
        }));
      },

      // ── Reconcile (called on reconnect) ──────────────────
      reconcile: async () => {
        // Force re-fetch — bypass coalescing since this is explicit server truth sync
        await Promise.all([
          get().fetchDailyVisits(undefined, { force: true }),
          get().fetchVisits(),
          get().loadConsultationStats(undefined, { force: true }),
        ]);
      },

      // ── Consultation Stats (for waiting-time prediction) ─
      loadConsultationStats: async (date, opts = {}) => {
        const doFetch = async () => {
          get().setLoading('stats', true);
          try {
            const data = await fetchConsultationStats(date);
            set({ consultationStats: data || { doctors: {} } });
            return data;
          } catch (err) {
            // Non-critical — prediction engine falls back to defaults
            console.warn('[VisitStore] Failed to load consultation stats:', err);
            set({ consultationStats: { doctors: {} } });
          } finally {
            get().setLoading('stats', false);
          }
        };
        return opts.force ? doFetch() : coalesce(DATA_KEYS.stats, doFetch);
      },

      // ── Fetch Doctors ────────────────────────────────────
      fetchDoctors: async (opts = {}) => {
        const doFetch = async () => {
          get().setLoading('doctors', true);
          try {
            const data = await fetchDoctors();
            set({ doctors: data.doctors || [], error: null });
          } catch (err) {
            set({ error: err.message });
          } finally {
            get().setLoading('doctors', false);
          }
        };
        return opts.force ? doFetch() : coalesce(DATA_KEYS.doctors, doFetch);
      },

      // ── Patient Lookup ───────────────────────────────────
      // `_patientSearchSeq` guards against a STALE response overwriting a
      // newer one. Type number A then change to B: if A's request resolves
      // last, A's patients are listed under B's number and picking one loads
      // the WRONG patient's record — name, allergies, consent and all. The
      // caller's debounce narrows that window but cannot close it; only
      // comparing against the latest query can. Same fix as AdmitPatientForm.
      _patientSearchSeq: 0,
      // EVERY path that abandons a lookup must bump the sequence, not just the
      // one that starts a new search. QA found two doors left open: this
      // short-number early return, and clearPatientLookup() below. Either one
      // leaves an in-flight response able to land afterwards and repopulate
      // the list — which is the same wrong-patient bug through a side entrance.
      _bumpPatientSearchSeq: () => {
        const seq = get()._patientSearchSeq + 1;
        set({ _patientSearchSeq: seq });
        return seq;
      },
      searchPatient: async (mobile) => {
        if (!mobile || mobile.length < 10) {
          // abandon anything in flight, and do not strand the spinner
          get()._bumpPatientSearchSeq();
          set({ patientLookup: null });
          get().setLoading('patient', false);
          return;
        }
        const seq = get()._bumpPatientSearchSeq();
        get().setLoading('patient', true);
        try {
          const data = await searchPatientByMobile(mobile);
          if (get()._patientSearchSeq !== seq) return;   // superseded
          // data is now an array of patients (may be empty — keep [] so UI can distinguish "not found" from "not searched")
          set({ patientLookup: Array.isArray(data) ? data : [], error: null });
        } catch (err) {
          if (get()._patientSearchSeq !== seq) return;
          set({ patientLookup: null, error: err.message });
        } finally {
          if (get()._patientSearchSeq === seq) get().setLoading('patient', false);
        }
      },

      // Bumps the sequence so an in-flight response cannot land after a
      // deliberate clear. The worst reachable path was RegisterVisitForm
      // calling this after a successful register to reset for the NEXT
      // patient: the previous patient's lookup then arrived on the blank
      // form — the owner's "fields flowing over to another patients",
      // verbatim, through a different door.
      clearPatientLookup: () => {
        get()._bumpPatientSearchSeq();
        set({ patientLookup: null });
        get().setLoading('patient', false);
      },

      // ── Register Visit (OPTIMISTIC) ─────────────────────
      registerVisit: async (payload) => {
        get().setLoading('register', true);
        set({ error: null, successMessage: null, createdVisit: null });

        try {
          const data = await registerVisit(payload);

          // Add to local lists immediately
          const visit = data.visit || data;
          const visitId = visit.id || visit.visit_id;

          set((s) => ({
            createdVisit: data,
            successMessage: 'Visit registered successfully',
            error: null,
            // Add to dailyVisits if not already there
            dailyVisits: s.dailyVisits.some((v) => (v.id || v.visit_id) === visitId)
              ? s.dailyVisits
              : [...s.dailyVisits, visit],
          }));

          // Track this registration so fetchDailyVisits won't overwrite it
          _recentRegistrations.push({ id: visitId, addedAt: Date.now() });

          // Emit event for other tabs/users
          eventBus.emit({
            type: REALTIME_EVENTS.VISIT_CREATED,
            payload: { visit },
            source: 'local',
            timestamp: Date.now(),
            eventId: `local_visit_created_${visitId}_${Date.now()}`,
          });

          return data;
        } catch (err) {
          set({ error: err.message });
          throw err;
        } finally {
          get().setLoading('register', false);
        }
      },

      // ── Fetch Visits ─────────────────────────────────────
      fetchVisits: async (filters = {}) => {
        get().setLoading('visits', true);
        try {
          const { scope } = get();
          const merged = { ...filters };
          if (scope.doctorId && !merged.doctor_id) {
            merged.doctor_id = scope.doctorId;
          }
          const data = await fetchVisits(merged);
          set({ visits: data.visits || [], error: null });
        } catch (err) {
          set({ error: err.message });
        } finally {
          get().setLoading('visits', false);
        }
      },

      // ── Fetch Daily Visits (coalesced) ───────────────────
      fetchDailyVisits: async (dateOrFilters, opts = {}) => {
        const doFetch = async () => {
          get().setLoading('daily', true);
          try {
            const { scope } = get();
            const filters =
              typeof dateOrFilters === 'string'
                ? { date: dateOrFilters }
                : { ...(dateOrFilters || {}) };
            if (scope.doctorId && !filters.doctor_id) {
              filters.doctor_id = scope.doctorId;
            }
            const data = await fetchDailyVisits(filters);

            // Merge: preserve optimistically-added visits that the GSI
            // hasn't propagated yet (DynamoDB eventual consistency)
            const now = Date.now();
            _recentRegistrations = _recentRegistrations.filter(
              (r) => now - r.addedAt < REGISTRATION_GUARD_MS
            );
            const guardedIds = new Set(_recentRegistrations.map((r) => r.id));

            set((s) => {
              const serverVisits = data.visits || [];
              const queueCount = serverVisits.filter(v => v.status === 'QUEUE').length;
              console.log(`[VisitStore] fetchDailyVisits: ${serverVisits.length} visits (${queueCount} in QUEUE), guarded=${guardedIds.size}`);
              if (guardedIds.size === 0) {
                return { dailyVisits: serverVisits, error: null };
              }
              // Keep locally-added visits missing from GSI response
              const serverIds = new Set(
                serverVisits.map((v) => v.id || v.visit_id)
              );
              const preserved = s.dailyVisits.filter((v) => {
                const id = v.id || v.visit_id;
                return guardedIds.has(id) && !serverIds.has(id);
              });
              return {
                dailyVisits: [...serverVisits, ...preserved],
                error: null,
              };
            });
          } catch (err) {
            set({ error: err.message });
          } finally {
            get().setLoading('daily', false);
          }
        };

        // force bypasses coalescing (hydrator fallback path); otherwise
        // concurrent callers piggyback on one in-flight request.
        return opts.force ? doFetch() : coalesce(DATA_KEYS.daily, doFetch);
      },

      // ── Update Visit (OPTIMISTIC) ────────────────────────
      updateVisit: async (visitId, updates) => {
        get().setLoading('update', true);

        // ── Optimistic: apply all updates instantly ──
        const optimistic = createOptimisticUpdate(get, set, {}, ['dailyVisits', 'visits']);

        if (Object.keys(updates).length > 0) {
          const patchVisit = (list) =>
            list.map((v) =>
              (v.id || v.visit_id) === visitId
                ? { ...v, ...updates }
                : v
            );
          set((s) => ({
            dailyVisits: patchVisit(s.dailyVisits),
            visits: patchVisit(s.visits),
          }));
        }

        try {
          await updateVisit(visitId, updates);

          const statusLabel = updates.status
            ? `Visit ${
                updates.status === 'IN_PROGRESS'
                  ? 'started'
                  : updates.status === 'COMPLETED'
                  ? 'completed'
                  : updates.status === 'CANCELLED'
                  ? 'cancelled'
                  : updates.status === 'SKIPPED'
                  ? 'skipped'
                  : 'updated'
              }`
            : 'Visit updated';
          set({ successMessage: statusLabel });

          // Emit event for other tabs/users
          const eventType = updates.status
            ? statusToEventType(updates.status)
            : REALTIME_EVENTS.VISIT_UPDATED;

          eventBus.emit({
            type: eventType,
            payload: {
              visitId,
              newStatus: updates.status,
              previousStatus: optimistic.snapshot.dailyVisits?.find(
                (v) => (v.id || v.visit_id) === visitId
              )?.status,
              visit: { id: visitId, ...updates },
            },
            source: 'local',
            timestamp: Date.now(),
            eventId: `local_visit_update_${visitId}_${Date.now()}`,
          });
        } catch (err) {
          // ── Rollback optimistic update on error ──
          optimistic.rollback();
          set({ error: err.message });
        } finally {
          get().setLoading('update', false);
        }
      },

      // ── Skip Visit (OPTIMISTIC) ─────────────────────────
      skipVisit: async (visitId, skippedByRole) => {
        get().setLoading('update', true);

        const optimistic = createOptimisticUpdate(get, set, {}, ['dailyVisits', 'visits']);

        // Optimistic: mark as SKIPPED immediately
        const patchVisit = (list) =>
          list.map((v) =>
            (v.id || v.visit_id) === visitId
              ? { ...v, status: 'SKIPPED', was_skipped: true }
              : v
          );
        set((s) => ({
          dailyVisits: patchVisit(s.dailyVisits),
          visits: patchVisit(s.visits),
        }));

        try {
          await skipVisit(visitId, skippedByRole);
          set({ successMessage: 'Patient skipped' });

          eventBus.emit({
            type: REALTIME_EVENTS.VISIT_SKIPPED,
            payload: {
              visitId,
              newStatus: 'SKIPPED',
              previousStatus: optimistic.snapshot.dailyVisits?.find(
                (v) => (v.id || v.visit_id) === visitId
              )?.status,
              visit: { id: visitId, status: 'SKIPPED', was_skipped: true },
            },
            source: 'local',
            timestamp: Date.now(),
            eventId: `local_visit_skipped_${visitId}_${Date.now()}`,
          });
        } catch (err) {
          optimistic.rollback();
          set({ error: err.message });
        } finally {
          get().setLoading('update', false);
        }
      },

      // ── Requeue Visit (OPTIMISTIC) ──────────────────────
      requeueVisit: async (visitId) => {
        get().setLoading('update', true);

        const optimistic = createOptimisticUpdate(get, set, {}, ['dailyVisits', 'visits']);

        // Optimistic: mark as QUEUE immediately (queue_number will be corrected by server response)
        const patchVisit = (list) =>
          list.map((v) =>
            (v.id || v.visit_id) === visitId
              ? { ...v, status: 'QUEUE' }
              : v
          );
        set((s) => ({
          dailyVisits: patchVisit(s.dailyVisits),
          visits: patchVisit(s.visits),
        }));

        try {
          const result = await requeueVisit(visitId);
          // Apply server-assigned queue_number
          const patchWithServer = (list) =>
            list.map((v) =>
              (v.id || v.visit_id) === visitId
                ? { ...v, ...result, status: 'QUEUE' }
                : v
            );
          set((s) => ({
            dailyVisits: patchWithServer(s.dailyVisits),
            visits: patchWithServer(s.visits),
            successMessage: 'Patient requeued',
          }));

          eventBus.emit({
            type: REALTIME_EVENTS.VISIT_REQUEUED,
            payload: {
              visitId,
              newStatus: 'QUEUE',
              visit: result,
              newQueueNumber: result.queue_number,
            },
            source: 'local',
            timestamp: Date.now(),
            eventId: `local_visit_requeued_${visitId}_${Date.now()}`,
          });
        } catch (err) {
          optimistic.rollback();
          set({ error: err.message });
        } finally {
          get().setLoading('update', false);
        }
      },

      // ── Derived: group daily visits by doctor ────────────
      getDoctorGroups: () => {
        const { dailyVisits } = get();
        const map = {};

        // Name lookup: any doctor appearing as a current primary in
        // today's visits is known by name. Used to resolve sender names
        // for shadow listings since referral_history hops may not carry
        // from_doctor_name.
        const doctorNameById = {};
        (dailyVisits || []).forEach((v) => {
          const id = v.doctor_member_id || v.visit_doctor_id;
          if (id && v.doctor_name) doctorNameById[id] = v.doctor_name;
        });

        const ensureGroup = (docId, fallbackName) => {
          if (!map[docId]) {
            map[docId] = {
              doctor_id: docId,
              doctor_name: doctorNameById[docId] || fallbackName || 'Unknown',
              queue: 0,
              in_progress: 0,
              completed: 0,
              cancelled: 0,
              skipped: 0,
              forwarded_out: 0,
              total: 0,
              visits: [],
              _shadowSeen: new Set(),
            };
          }
          return map[docId];
        };

        (dailyVisits || []).forEach((v) => {
          const primaryId = v.doctor_member_id || v.visit_doctor_id || 'unknown';
          const g = ensureGroup(primaryId, v.doctor_name);
          g.total++;
          g.visits.push(v);
          if (v.status === 'QUEUE') g.queue++;
          else if (v.status === 'IN_PROGRESS') g.in_progress++;
          else if (v.status === 'COMPLETED') g.completed++;
          else if (v.status === 'CANCELLED') g.cancelled++;
          else if (v.status === 'SKIPPED') g.skipped++;

          // Shadow listings — surface forwarded visits under the
          // sender's group so their daily contribution stays credited.
          // Send Visit is a transfer of care; the sender no longer has
          // primary or coworker access on the visit, but the daily
          // dashboard should still acknowledge they handled it.
          const history = Array.isArray(v.referral_history) ? v.referral_history : [];
          for (const hop of history) {
            const senderId = hop && hop.from_doctor_id;
            if (!senderId) continue;
            // RULE 1 — skip if sender is the current primary. Handles
            // ping-pong (A→B→A): visit forwarded back to its original
            // sender shouldn't list twice under their group.
            if (senderId === primaryId) continue;
            const sg = ensureGroup(senderId, hop.from_doctor_name);
            // RULE 2 — dedup by visit_id within a shadow group. Handles
            // multi-bounce (A→B→A→B) where Dr A appears as sender twice.
            if (sg._shadowSeen.has(v.visit_id)) continue;
            sg._shadowSeen.add(v.visit_id);
            sg.forwarded_out++;
            sg.visits.push({ ...v, _is_forwarded_out: true });
          }
        });

        // Strip internal dedup Set before returning.
        return Object.values(map).map(({ _shadowSeen, ...rest }) => rest);
      },

      // ── Derived: clinic summary from daily visits ───────
      getClinicSummary: () => {
        const { dailyVisits } = get();
        return (dailyVisits || []).reduce(
          (acc, v) => {
            acc.total++;
            if (v.status === 'QUEUE') acc.queue++;
            else if (v.status === 'IN_PROGRESS') acc.in_progress++;
            else if (v.status === 'COMPLETED') acc.completed++;
            else if (v.status === 'CANCELLED') acc.cancelled++;
            else if (v.status === 'SKIPPED') acc.skipped++;
            return acc;
          },
          { total: 0, queue: 0, in_progress: 0, completed: 0, cancelled: 0, skipped: 0 }
        );
      },

      // ── Doctor Availability ─────────────────────────────
      updateDoctorAvailability: async (memberId, availability) => {
        // Optimistic update
        const prev = get().doctors;
        set((s) => ({
          doctors: (s.doctors || []).map((d) =>
            (d.member_id || d.doctor_id) === memberId
              ? { ...d, availability }
              : d
          ),
        }));

        try {
          const result = await apiUpdateDoctorAvailability(memberId, availability);
          if (!result.success) {
            // Rollback
            set({ doctors: prev, error: result.error });
            return;
          }

          // Emit for cross-tab
          eventBus.emit({
            type: REALTIME_EVENTS.DOCTOR_STATUS_CHANGED,
            payload: { memberId, availability },
            source: 'local',
            timestamp: Date.now(),
            eventId: `local_doctor_status_${memberId}_${Date.now()}`,
          });
        } catch (err) {
          set({ doctors: prev, error: err.message });
        }
      },

      // Helper: get a doctor's current availability
      getDoctorAvailability: (memberId) => {
        const doc = (get().doctors || []).find(
          (d) => (d.member_id || d.doctor_id) === memberId
        );
        return doc?.availability || 'available';
      },

      // ══════════════════════════════════════════════════════
      // FUTURE: Nurse-to-Doctor OPD Assignment
      // ──────────────────────────────────────────────────────
      // State shape:
      //   assignedNurses: { [doctorId]: [nurseId, ...] }
      //
      // Actions:
      //   assignNurseToDoctor(doctorId, nurseId)
      //   removeNurseFromDoctor(doctorId, nurseId)
      //   getAssignedDoctor(nurseId) → doctorId | null
      //
      // Backend: POST /api/organisation/nurse-assignments
      //          DynamoDB SK: NURSEASSIGN#{doctor_id}#{nurse_id}
      //
      // When implemented, NurseView in SmartWorkflowWidget
      // will auto-filter to show only assigned doctor's patients.
      // ══════════════════════════════════════════════════════
    }),
    {
      storeName: 'visit',
      eventHandlers: visitEventHandlers,
    }
  )
);

export default useVisitStore;
